import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useSuperAdminAuth } from '../contexts/SuperAdminAuthContext';

const SIDEBAR_COLLAPSED_KEY = 'superadmin-sidebar-collapsed';

const navItems = [
    { path: '/super-admin', label: 'Dashboard', icon: '📊' },
    { path: '/super-admin/companies', label: 'Companies', icon: '🏢' },
    { path: '/super-admin/plans', label: 'Plans', icon: '💰' },
    { path: '/super-admin/modules', label: 'Modules', icon: '🧩' },
    { path: '/super-admin/audit-logs', label: 'Audit Logs', icon: '📋' },
];

function SuperAdminLoginForm() {
    const { signIn } = useSuperAdminAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await signIn(email, password);
        } catch (err: any) {
            setError(err.message || 'Failed to sign in');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
            <div className="max-w-md w-full space-y-8 p-8 bg-gray-900 rounded-xl border border-gray-800 shadow-xl">
                <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-xl bg-purple-600 flex items-center justify-center text-3xl">
                        ⭐
                    </div>
                    <h2 className="mt-6 text-3xl font-bold text-white">Super Admin</h2>
                    <p className="mt-2 text-sm text-gray-400">Sign in to access the admin panel</p>
                </div>

                <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="root@beyondseek.com"
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>

                </form>

                <div className="text-center">
                    <a href="/landing" className="text-sm text-purple-400 hover:text-purple-300 transition-colors">
                        ← Back to Landing Page
                    </a>
                </div>
            </div>
        </div>
    );
}

export default function SuperAdminLayout() {
    const { user, logout, loading } = useSuperAdminAuth();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
    }, [isCollapsed]);

    const handleLogout = async () => {
        await logout();
        // Use window.location for full page reload to properly reset state
        window.location.href = '/landing';
    };

    // Show loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
                <div className="text-white text-xl">Loading...</div>
            </div>
        );
    }

    // Show login form if not authenticated
    if (!user) {
        return <SuperAdminLoginForm />;
    }

    return (
        <div className="min-h-screen flex bg-gray-950">
            {/* Sidebar */}
            <aside className={`${isCollapsed ? 'w-16' : 'w-64'} bg-gray-900 shadow-lg border-r border-gray-800 flex flex-col transition-all duration-300`}>
                {/* Header */}
                <div className="p-4 border-b border-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                            ⭐
                        </div>
                        {!isCollapsed && (
                            <div className="overflow-hidden">
                                <h1 className="font-bold text-white whitespace-nowrap">Stellar Admin</h1>
                                <p className="text-xs text-gray-400 whitespace-nowrap">Super Admin Panel</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 py-4 px-2">
                    <div className="space-y-1">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path ||
                                (item.path !== '/super-admin' && location.pathname.startsWith(item.path));
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    title={isCollapsed ? item.label : undefined}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${isActive
                                        ? 'bg-purple-600 hover:bg-purple-600 text-white shadow-md cursor-default'
                                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                                        } ${isCollapsed ? 'justify-center' : ''}`}
                                >
                                    <span className="text-lg flex-shrink-0">{item.icon}</span>
                                    {!isCollapsed && <span className="font-medium whitespace-nowrap">{item.label}</span>}
                                </Link>
                            );
                        })}
                    </div>
                </nav>

                {/* Collapse Toggle */}
                <div className="px-2 py-2 border-t border-gray-800">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg transition-colors"
                    >
                        <span className="text-lg">{isCollapsed ? '→' : '←'}</span>
                        {!isCollapsed && <span>Collapse</span>}
                    </button>
                </div>

                {/* User Section */}
                <div className={`p-4 border-t border-gray-800 ${isCollapsed ? 'px-2' : ''}`}>
                    <div className={`flex items-center gap-3 mb-3 ${isCollapsed ? 'justify-center' : ''}`}>
                        <div
                            className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold flex-shrink-0"
                            title={isCollapsed ? (user?.name || 'Super Admin') : undefined}
                        >
                            {user?.name?.[0] || 'S'}
                        </div>
                        {!isCollapsed && (
                            <div className="flex-1 min-w-0 overflow-hidden">
                                <p className="font-medium text-white truncate">{user?.name || 'Super Admin'}</p>
                                <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={handleLogout}
                        title={isCollapsed ? 'Logout' : undefined}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors`}
                    >
                        <span>🚪</span>
                        {!isCollapsed && <span>Logout</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-auto bg-gray-950 text-white">
                <Outlet />
            </main>
        </div>
    );
}

